import React from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Container,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import SideNavbar from "../../../components/sideNavbar";

// Design kit for the complaint module.
//
// Everything visual lives here so the list, detail, form, reports and settings
// screens stay one coherent surface. Tokens follow the product's existing
// dashboard language (20px cards, hairline borders, indigo accent) rather than
// inventing a second look.

// Width of the app's fixed side navigation. The page shell subtracts it from
// the content width; setting only a left margin (as the older screens do) makes
// the page wider than the viewport and clips whatever sits furthest right.
export const NAV_WIDTH = 280;

// -- Tokens ----------------------------------------------------------------
// Professional, not flashy: real elevation and a confident type scale so the
// page has weight, but no gradients or oversized radii. Pills stay reserved for
// status chips, where a rounded badge reads as state rather than decoration.
export const T = {
  brand: "#4F46E5",
  brandLight: "#6366F1",
  brandSoft: "#EEF2FF",
  brandBorder: "#C7D2FE",

  // White page. With the page and the cards both white, a card can no longer
  // rely on contrasting against its background — so borders are a step darker
  // and every card carries elevation. Colour and structure do the work that a
  // grey backdrop used to do.
  page: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceMuted: "#F9FAFB",
  surfaceHeader: "#F9FAFB",
  track: "#F2F4F7",
  border: "#E4E7EC",
  borderStrong: "#D0D5DD",

  ink: "#101828",
  inkSecondary: "#475467",
  inkMuted: "#98A2B3",

  radiusCard: "12px",
  radiusTile: "10px",
  radiusControl: "8px",
  radiusPill: "999px",

  // Two-layer elevation reads as a lifted surface rather than a printed outline.
  shadow: "0 1px 3px rgba(16,24,40,0.10), 0 1px 2px rgba(16,24,40,0.06)",
  shadowHover: "0 6px 16px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.06)",
};

/** Tone palettes shared by stat tiles and any other accented surface. */
export const TONES = {
  neutral: { fg: "#4F46E5", bg: "#EEF2FF", accent: "#6366F1" },
  good: { fg: "#067647", bg: "#DCFAE6", accent: "#17B26A" },
  warning: { fg: "#B54708", bg: "#FEF0C7", accent: "#F79009" },
  critical: { fg: "#B42318", bg: "#FEE4E2", accent: "#F04438" },
  info: { fg: "#026AA2", bg: "#E0F2FE", accent: "#0BA5EC" },
};

/**
 * One type scale for the whole module. Sizes were bumped across the board —
 * the previous pass shrank labels to 0.68rem, which read as cramped and cheap.
 */
export const TYPE = {
  pageTitle: "1.35rem",
  cardTitle: "0.95rem",
  // 1.65rem overflowed the narrower tiles ("19h 44m" clipped to "19h 4…").
  statValue: "1.4rem",
  body: "0.875rem",
  small: "0.8rem",
  label: "0.75rem",
  micro: "0.72rem",
};

/** Small uppercase label used above values and on table headers. */
export const labelSx = {
  fontSize: TYPE.label,
  fontWeight: 600,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  color: T.inkMuted,
};

// Kept for backwards compatibility with earlier imports.
export const ACCENT = T.brandLight;

// -- Chart theme -----------------------------------------------------------
// Validated categorical palette: passes lightness band, chroma floor, adjacent
// CVD separation (worst dE 8.9) and normal-vision floor (worst dE 16.4) on a
// light surface. Five slots sit under 3:1 contrast, so every report also ships
// a table view — that is the required relief, not an optional extra.
export const SERIES = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
];

export const CHART = {
  grid: "#EEF0F6",
  tick: { fontSize: 11, fill: T.inkSecondary },
  axisProps: { axisLine: false, tickLine: false },
};

/** Dark tooltip card, matching the product dashboard's chart tooltips. */
export const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        bgcolor: "#111827",
        color: "#fff",
        px: 1.5,
        py: 1,
        borderRadius: "10px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        minWidth: 120,
      }}
    >
      {label !== undefined && (
        <Typography sx={{ fontSize: "0.7rem", color: "#9CA3AF", mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((entry) => (
        <Stack
          key={entry.dataKey}
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "2px",
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: "0.75rem", color: "#E5E7EB", flexGrow: 1 }}
          >
            {entry.name}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700 }}>
            {formatter ? formatter(entry.value) : entry.value}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

// -- Status / priority / SLA palettes --------------------------------------
// Reserved status colors. Each always ships with its text label, so state is
// never carried by color alone.
export const STATUS_COLORS = {
  New: { bg: "#EEF2FF", fg: "#4338CA", dot: "#6366F1" },
  "In Progress": { bg: "#E0F2FE", fg: "#0369A1", dot: "#0EA5E9" },
  Escalated: { bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B" },
  Resolved: { bg: "#DCFCE7", fg: "#15803D", dot: "#10B981" },
  Closed: { bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
  Reopened: { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" },
};

export const PRIORITY_COLORS = {
  Low: { bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
  Medium: { bg: "#E0F2FE", fg: "#0369A1", dot: "#0EA5E9" },
  High: { bg: "#FFEDD5", fg: "#C2410C", dot: "#F97316" },
  Critical: { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" },
};

export const SLA_COLORS = {
  on_track: { bg: "#DCFCE7", fg: "#15803D", dot: "#10B981", label: "On track" },
  at_risk: { bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B", label: "At risk" },
  breached: { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444", label: "Breached" },
  met: { bg: "#DCFCE7", fg: "#15803D", dot: "#10B981", label: "Met" },
  unknown: { bg: "#F1F5F9", fg: "#64748B", dot: "#CBD5E1", label: "No SLA" },
};

// -- Layout ----------------------------------------------------------------

/**
 * Standard page frame for every screen in the module.
 *
 * The content column is both offset by the nav AND narrowed by it. Offsetting
 * alone leaves the column a full viewport wide, so it runs off the right edge —
 * which is what clipped the toolbar buttons and the last grid column. `minWidth: 0`
 * lets flex/grid children shrink instead of forcing the page wider, and the
 * horizontal clip stops any single wide child scrolling the whole document.
 */
export const PageShell = ({ children, maxWidth = "xl" }) => (
  <Box sx={{ bgcolor: T.page, minHeight: "100vh", width: "100%", maxWidth: "100vw", overflowX: "hidden", position: "relative" }}>
    <SideNavbar />
    <Box
      component="main"
      sx={{
        ml: { xs: 0, md: `${NAV_WIDTH}px` },
        width: { xs: "100%", md: `calc(100% - ${NAV_WIDTH}px)` },
        minWidth: 0,
        maxWidth: { xs: "100%", md: `calc(100% - ${NAV_WIDTH}px)` },
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Container maxWidth={maxWidth} sx={{ py: 3, px: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
        {children}
      </Container>
    </Box>
  </Box>
);

// -- Primitives ------------------------------------------------------------
export const GlassCard = styled(Card)({
  background: T.surface,
  borderRadius: T.radiusCard,
  border: `1px solid ${T.border}`,
  boxShadow: T.shadow,
});

/** Card that lifts slightly on hover — for anything clickable. */
export const HoverCard = styled(GlassCard)({
  transition: "box-shadow .2s ease",
  "&:hover": { boxShadow: T.shadowHover },
});

export const IconTile = styled(Box)(({ tint }) => ({
  backgroundColor: tint || T.surfaceMuted,
  borderRadius: T.radiusTile,
  padding: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 0,
  // Explicit, because a caller that sets width/height would otherwise have the
  // padding added on top wherever box-sizing has not been reset globally.
  boxSizing: "border-box",
}));

/** Pill chip with a leading dot — the module's standard state marker. */
const DotChip = ({ palette, label, tooltip }) => {
  const chip = (
    <Chip
      size="small"
      label={
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: palette.dot,
              flexShrink: 0,
            }}
          />
          <span>{label}</span>
        </Stack>
      }
      sx={{
        bgcolor: palette.bg,
        color: palette.fg,
        fontWeight: 600,
        fontSize: TYPE.micro,
        height: 26,
        borderRadius: T.radiusPill,
        "& .MuiChip-label": { px: 1.1 },
      }}
    />
  );

  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

export const StatusChip = ({ status }) => (
  <DotChip
    palette={STATUS_COLORS[status] || STATUS_COLORS.Closed}
    label={status}
  />
);

export const PriorityChip = ({ priority }) => (
  <DotChip
    palette={PRIORITY_COLORS[priority] || PRIORITY_COLORS.Medium}
    label={priority}
  />
);

export const SlaChip = ({ slaStatus }) => {
  if (!slaStatus)
    return <DotChip palette={SLA_COLORS.unknown} label="No SLA" />;

  const palette = SLA_COLORS[slaStatus.state] || SLA_COLORS.unknown;
  const { minutesRemaining, state, percentUsed } = slaStatus;

  let label = palette.label;
  if (state === "on_track" || state === "at_risk") {
    label = `${formatDuration(minutesRemaining)} left`;
  } else if (state === "breached" && minutesRemaining < 0) {
    label = `${formatDuration(Math.abs(minutesRemaining))} over`;
  }

  return (
    <DotChip
      palette={palette}
      label={label}
      tooltip={
        percentUsed !== null && percentUsed !== undefined
          ? `${palette.label} — ${percentUsed}% of the SLA window used`
          : palette.label
      }
    />
  );
};

/**
 * SLA consumption as a meter. A ratio against a limit reads better as a track
 * than as a chart, and the remaining time stays legible as text.
 */
export const SlaMeter = ({ slaStatus, showLabel = true }) => {
  if (!slaStatus || slaStatus.state === "unknown") return null;

  const palette = SLA_COLORS[slaStatus.state] || SLA_COLORS.unknown;
  const used = Math.max(0, Math.min(100, slaStatus.percentUsed ?? 0));

  return (
    <Box>
      {showLabel && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Typography sx={{ fontSize: "0.7rem", color: T.inkSecondary }}>
            {palette.label}
          </Typography>
          <Typography
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: palette.fg }}
          >
            {used}%
          </Typography>
        </Stack>
      )}
      <LinearProgress
        variant="determinate"
        value={used}
        sx={{
          height: 6,
          borderRadius: 999,
          bgcolor: T.border,
          "& .MuiLinearProgress-bar": {
            bgcolor: palette.dot,
            borderRadius: 999,
          },
        }}
      />
    </Box>
  );
};

/**
 * KPI tile. The figure leads, the icon carries the tone, and an optional `sub`
 * line gives the number context so the tile says something rather than just
 * showing a digit.
 */
export const StatTile = ({
  label,
  value,
  icon: Icon,
  tone = "neutral",
  hint,
  sub,
  onClick,
}) => {
  const tint = TONES[tone] || TONES.neutral;

  const body = (
    <GlassCard
      onClick={onClick}
      sx={{
        position: "relative",
        overflow: "hidden",
        // Tight horizontal padding buys the label the room it needs when six
        // tiles share the row.
        pl: 1.75,
        pr: 1.25,
        py: 1.25,
        // Fixed, medium height. Every line inside is noWrap, so a set height is
        // safe and keeps the row uniform without depending on grid stretch.
        height: 80,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
        // Colour spine. With the page now white, this is what separates one tile
        // from the next and signals at a glance which figures are healthy.
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: tint.accent,
        },
        ...(onClick
          ? {
              "&:hover": {
                boxShadow: T.shadowHover,
                borderColor: T.brandBorder,
                transform: "translateY(-1px)",
              },
            }
          : {}),
      }}
    >
      {Icon && (
        <IconTile
          tint={tint.bg}
          sx={{ color: tint.fg, width: 34, height: 34, flexShrink: 0 }}
        >
          <Icon size={16} />
        </IconTile>
      )}
      {/* Tight vertical rhythm: the label, figure and context line stack with no
          stray leading, so the card hugs its content instead of leaving a gap. */}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        {/* Tighter than the shared label style: "AVG RESOLUTION" is the longest
            caption and has to fit a sixth of the row without clipping. */}
        <Typography
          sx={{ ...labelSx, fontSize: "0.7rem", letterSpacing: "0.3px", lineHeight: 1.3 }}
          noWrap
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: TYPE.statValue,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
          }}
          noWrap
        >
          {value ?? "—"}
        </Typography>
        {sub && (
          <Typography
            sx={{ fontSize: TYPE.micro, color: T.inkMuted, lineHeight: 1.3 }}
            noWrap
          >
            {sub}
          </Typography>
        )}
      </Box>
    </GlassCard>
  );

  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
};

/**
 * Row of KPI tiles.
 *
 * Deliberately CSS Grid rather than MUI's Grid: MUI applies a negative margin
 * plus `width: calc(100% + spacing)` to a spaced container, which widened this
 * row past its parent and ate the gap below it, leaving the tiles flush against
 * the toolbar. A plain grid with `gap` has neither problem and gives every tile
 * equal height for free.
 */
export const StatRow = ({ children, columns = 6 }) => (
  <Box
    sx={{
      display: "grid",
      // A fixed column count so the whole set stays on one line at desktop
      // widths. `minmax(0, 1fr)` is what lets the columns shrink evenly — with
      // a plain `1fr` the tiles refuse to go below their content width and the
      // last one wraps onto a second row.
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        sm: "repeat(3, minmax(0, 1fr))",
        lg: `repeat(${columns}, minmax(0, 1fr))`,
      },
      // Tiles size to their own content rather than stretching to the tallest
      // row, which is what left them with large empty areas.
      alignItems: "start",
      gap: 1.5,
      mb: 3,
    }}
  >
    {children}
  </Box>
);

/** Deterministic initials avatar, so the same person keeps the same colour. */
export const PersonAvatar = ({ name, size = 28 }) => {
  const label = (name || "?").trim();
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  let hash = 0;
  for (let i = 0; i < label.length; i += 1)
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  const color = SERIES[Math.abs(hash) % SERIES.length];

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: `${color}1A`,
        color,
        fontSize: size * 0.38,
        fontWeight: 700,
      }}
    >
      {initials || "?"}
    </Avatar>
  );
};

/** Shown instead of an empty table or grid. */
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  dense = false,
}) => (
  <Box sx={{ textAlign: "center", py: dense ? 5 : 8, px: 3 }}>
    {Icon && (
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "14px",
          bgcolor: T.brandSoft,
          color: T.brand,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          boxSizing: "border-box",
        }}
      >
        <Icon size={26} />
      </Box>
    )}
    <Typography
      sx={{ fontSize: TYPE.cardTitle, fontWeight: 700, color: T.ink, mb: 0.75 }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        sx={{
          fontSize: TYPE.small,
          color: T.inkSecondary,
          maxWidth: 440,
          mx: "auto",
          lineHeight: 1.6,
        }}
      >
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

/** Placeholder rows while a list loads — steadier than a bare spinner. */
export const ListSkeleton = ({ rows = 6, height = 46 }) => (
  <Box sx={{ p: 2 }}>
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        height={height}
        sx={{ mb: 1, borderRadius: T.radiusControl, bgcolor: "#F3F4F8" }}
      />
    ))}
  </Box>
);

/** Same geometry as StatRow, so the layout does not jump once data arrives. */
export const TileSkeleton = ({ count = 6 }) => (
  <StatRow columns={count}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        height={80}
        sx={{ borderRadius: T.radiusCard, bgcolor: "#F1F2F6" }}
      />
    ))}
  </StatRow>
);

/** Page heading used at the top of every screen in the module. */
export const PageHeader = ({
  title,
  subtitle,
  actions,
  breadcrumb,
  icon: Icon,
}) => (
  // A rule under the header gives the title block something to sit on now that
  // the page has no background tone of its own.
  <Box sx={{ mb: 3, pb: 2.5, borderBottom: `1px solid ${T.border}` }}>
    {breadcrumb}
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
    >
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        {Icon && (
          <IconTile
            tint={T.brandSoft}
            sx={{ color: T.brand, width: 42, height: 42, flexShrink: 0 }}
          >
            <Icon size={21} />
          </IconTile>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: TYPE.pageTitle,
              fontWeight: 700,
              color: T.ink,
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 0.35 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ flexShrink: 0 }}
        >
          {actions}
        </Stack>
      )}
    </Stack>
  </Box>
);

export const SectionTitle = ({ children, icon: Icon, action, sx }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: 1.75, ...sx }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {Icon && <Icon size={16} color={T.brand} />}
      <Typography
        sx={{ fontSize: TYPE.cardTitle, fontWeight: 700, color: T.ink }}
      >
        {children}
      </Typography>
    </Stack>
    {action}
  </Stack>
);

export const Field = ({ label, value, sx }) => (
  <Box sx={{ mb: 1.75, ...sx }}>
    <Typography sx={{ ...labelSx, display: "block", mb: 0.35 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: TYPE.body,
        fontWeight: 500,
        color: T.ink,
        wordBreak: "break-word",
        lineHeight: 1.5,
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </Typography>
  </Box>
);

/** Header strip for a card that contains a table or list. */
export const CardHeader = ({ title, subtitle, action, icon: Icon }) => (
  <Box
    sx={{
      px: 2.5,
      py: 2,
      borderBottom: `1px solid ${T.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
    }}
  >
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      {Icon && <Icon size={16} color={T.brand} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: TYPE.cardTitle, fontWeight: 700, color: T.ink }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mt: 0.15 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Box>
);

/** Shared button styling so every primary action in the module matches. */
export const primaryButtonSx = {
  borderRadius: T.radiusControl,
  px: 2.25,
  py: 0.85,
  bgcolor: T.brand,
  fontSize: TYPE.small,
  boxShadow: "0 1px 2px rgba(16,24,40,0.10)",
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "#4338CA",
    boxShadow: "0 2px 6px rgba(79,70,229,0.28)",
  },
};

export const ghostButtonSx = {
  borderRadius: T.radiusControl,
  px: 2,
  py: 0.85,
  fontSize: TYPE.small,
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
  color: T.inkSecondary,
  bgcolor: T.surface,
  borderColor: T.borderStrong,
  boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
  "&:hover": { borderColor: T.brand, color: T.brand, bgcolor: T.brandSoft },
};

// -- Formatters ------------------------------------------------------------

/** Minutes as a compact human duration: 45m, 3h 20m, 2d 4h. */
export function formatDuration(minutes) {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes))
    return "—";

  const mins = Math.abs(Math.round(minutes));
  if (mins < 60) return `${mins}m`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const rem = mins % 60;
    return rem ? `${hours}h ${rem}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
}

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Relative time for timelines: "4m ago", "2h ago", "3d ago". */
export const formatRelative = (value) => {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";

  const diffMins = Math.round((Date.now() - then) / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
  return formatDate(value);
};

// -- Currency ---------------------------------------------------------------
// One currency per workspace, set by a CRM Admin. It is held module-side rather
// than passed through every component because it is a single value that changes
// only in settings — threading it through ~30 call sites would add noise without
// adding correctness.

export const CURRENCY_CONFIG = {
  INR: { code: "INR", symbol: "₹", label: "Indian Rupee", locale: "en-IN" },
  USD: { code: "USD", symbol: "$", label: "US Dollar", locale: "en-US" },
  AED: { code: "AED", symbol: "AED", label: "UAE Dirham", locale: "en-AE" },
};

let workspaceCurrency = "INR";

/** Called once when a screen loads workspace metadata or settings. */
export const setWorkspaceCurrency = (code) => {
  if (code && CURRENCY_CONFIG[code]) workspaceCurrency = code;
};

export const getWorkspaceCurrency = () => workspaceCurrency;

/** Just the symbol, for input adornments and inline captions. */
export const currencySymbol = (code) =>
  (CURRENCY_CONFIG[code || workspaceCurrency] || CURRENCY_CONFIG.INR).symbol;

/**
 * Formats an amount in the workspace currency.
 * @param {number} value
 * @param {string} [code] override, for the rare case a specific currency is known
 */
export const formatCurrency = (value, code) => {
  const amount = Number(value) || 0;
  const config = CURRENCY_CONFIG[code || workspaceCurrency] || CURRENCY_CONFIG.INR;

  // Whole units stay clean (₹1,250 not ₹1,250.00), but a fractional amount
  // shows both decimals — ₹99.50, never ₹99.5.
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;

  try {
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    // Intl can throw on an unknown locale in older runtimes.
    return `${config.symbol}${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
};

/** Compact figures for tiles: 1.2k, 3.4M. */
export const formatCompact = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  if (Math.abs(num) < 1000) return String(num);
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(num);
};

// -- Permissions -----------------------------------------------------------
export const can = (permissions, permission) =>
  Array.isArray(permissions) && permissions.includes(permission);

export const PERMISSION = {
  CREATE: "complaint.create",
  UPDATE: "complaint.update",
  DELETE: "complaint.delete",
  ASSIGN: "complaint.assign",
  FORWARD: "complaint.forward",
  STATUS_CHANGE: "complaint.status.change",
  RESOLVE: "complaint.resolve",
  CLOSE: "complaint.close",
  REOPEN: "complaint.reopen",
  SLA_MODIFY: "complaint.sla.modify",
  REFUND_APPROVE: "refund.approve",
  REFUND_PROCESS: "refund.process",
  NOTE_ADD: "notes.internal.add",
  NOTE_VIEW: "notes.internal.view",
  COMMUNICATION_LOG: "communication.log",
  ATTACHMENT_UPLOAD: "attachment.upload",
  QA_SCORE: "qa.score",
  CATEGORY_MANAGE: "category.manage",
  ROLE_MANAGE: "role.manage",
  SETTINGS_MANAGE: "settings.manage",
  REPORT_VIEW: "report.view",
  CUSTOMER_HISTORY: "customer.history.view",
};
